/* Native Shopify localization when published, explicitly scoped theme-interface fallback otherwise. */
(() => {
  'use strict';
  const locale = window.NvlLocale;
  const dictionaries = window.NvlInterfaceLocales;
  const get = (lang, key) => key.split('.').reduce((value, part) => value?.[part], dictionaries?.[lang]);
  const translate = (key, params = {}) => {
    let value = get(locale?.active || 'fr', key);
    if (value && typeof value === 'object') {
      const plural = new Intl.PluralRules(locale.active).select(Number(params.count || 0));
      value = value[plural] ?? value.other;
    }
    if (typeof value !== 'string') return null;
    return value.replace(/{{\s*(\w+)\s*}}/g, (token, name) => params[name] ?? token);
  };
  // Exact interface copies only, for aria attributes and buttons updated by product JavaScript.
  const knownCopy = new Map();
  const collect = (value, prefix = '') => Object.entries(value || {}).forEach(([key, text]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof text === 'object') collect(text, path);
    else if (!text.includes('{{')) knownCopy.set(text, path);
  });
  collect(dictionaries?.fr); collect(dictionaries?.ar);
  const setText = (element, text) => { if (text !== null && element.textContent !== text) element.textContent = text; };
  const apply = () => {
    if (!locale || !dictionaries) return;
    document.querySelectorAll('[data-nvl-locale]').forEach(button => {
      if (button.dataset.nvlLocale === locale.active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    if (locale.active === locale.native) return;
    document.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      let url;
      try { url = new URL(href, window.location.href); } catch (error) { return; }
      if (url.origin !== window.location.origin || /^\/(?:checkout|checkouts|localization)(?:\/|$)/.test(url.pathname)) return;
      url.searchParams.set('nvl_lang', locale.active);
      const target = url.pathname + url.search + url.hash;
      if (href !== target) link.setAttribute('href', target);
    });
    document.querySelectorAll('[data-nvl-i18n], [data-nvl-copy-fr]').forEach(element => {
      const custom = element.getAttribute(`data-nvl-copy-${locale.active}`);
      const translated = custom || translate(element.dataset.nvlI18n || '', {
        count: element.dataset.nvlCount, number: element.dataset.nvlNumber
      });
      setText(element, translated);
    });
    document.querySelectorAll('option[data-nvl-variant-title]').forEach(option => {
      const suffix = option.dataset.nvlVariantAvailable === 'false' ? ' · ' + translate('product.sold_out') : '';
      setText(option, option.dataset.nvlVariantTitle + suffix);
    });
    document.querySelectorAll('[data-nvl-price-current], [data-nvl-price-compare]').forEach(element => {
      const text = element.textContent.trim().replace(/\s+(DA|دج)$/u, ' ' + translate('general.da'));
      setText(element, text);
    });
    document.querySelectorAll('[aria-label], [placeholder], [title]').forEach(element => {
      if (element.closest('#easysell, #es-form-container')) return;
      ['aria-label', 'placeholder', 'title'].forEach(attribute => {
        const value = element.getAttribute(attribute);
        const key = knownCopy.get(value?.trim());
        if (key) { const translated = translate(key); if (translated && translated !== value) element.setAttribute(attribute, translated); }
      });
    });
    document.querySelectorAll('[data-nvl-jump-order], [data-nvl-native-submit], [data-nvl-availability]').forEach(element => {
      const key = knownCopy.get(element.textContent.trim());
      if (key) setText(element, translate(key));
    });
  };
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-nvl-locale]');
    if (!button) return;
    event.preventDefault();
    const selected = button.dataset.nvlLocale;
    const nativeCode = button.dataset.nativeCode;
    const url = new URL(window.location.href);
    if (nativeCode) {
      try { localStorage.removeItem('nvl-interface-language'); } catch (error) { /* Storage is optional. */ }
      url.searchParams.delete('nvl_lang');
      const form = button.form;
      const hidden = (name, value) => {
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = name; form.appendChild(input); }
        input.value = value;
      };
      hidden('language_code', nativeCode);
      hidden('return_to', url.pathname + url.search + url.hash);
      // Explicit input also supports localization handlers which serialize without the submitter.
      form.requestSubmit();
    } else {
      try { localStorage.setItem('nvl-interface-language', selected); } catch (error) { /* URL remains functional. */ }
      url.searchParams.set('nvl_lang', selected);
      window.location.assign(url.href);
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-nvl-sort]')) event.target.form.requestSubmit();
  });
  const start = () => {
    apply();
    if (locale?.active !== locale?.native) {
      let queued = false;
      const observer = new MutationObserver(() => {
        if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; apply(); }); }
      });
      // App internals are never translated; only explicit theme markers are changed by apply().
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', apply);
})();
